import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Map, ShieldCheck, Link2, Award, Globe2, UserPlus } from "lucide-react";
import { InfoTip, LastVerified } from "@/components/kit";

const API = process.env.REACT_APP_BACKEND_URL;
const STEP_CLS = { done: "k-tag-green", current: "k-tag-info", todo: "k-muted" };

// PATCH 28 — Big Wins bundle for a project: Journey timeline (BW5), Access nudge (BW6),
// Compliance Passport (BW2), Investor/Auditor share link (BW7), Switch Market (BW3),
// Reviewer invite from day one (BW10), Build Badge (BW12), tagline (BW13), budget meter (BW4).
export default function ProjectJourney({ projectId }) {
  const [journey, setJourney] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [budget, setBudget] = useState(null);
  const [passport, setPassport] = useState(null);
  const [links, setLinks] = useState([]);
  const [invite, setInvite] = useState("");
  const [switchJur, setSwitchJur] = useState("");
  const [delta, setDelta] = useState(null);
  const [showSwitchTip, setShowSwitchTip] = useState(false);

  const dismissSwitchTip = () => { setShowSwitchTip(false); try { localStorage.setItem("kadana_switch_market_tip_v1", "1"); } catch { /* ignore */ } };

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/journey`).then(r => setJourney(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/access-nudge`).then(r => setNudge(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/build-budget`).then(r => setBudget(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/share-links`).then(r => setLinks(r.data || [])).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  // SW15 — one-time tip pointing at Switch Market, shown once after the first successful build.
  useEffect(() => {
    try {
      const built = journey?.steps?.some((s) => s.key === "build" && s.status === "done");
      if (built && !localStorage.getItem("kadana_switch_market_tip_v1")) setShowSwitchTip(true);
    } catch { /* ignore */ }
  }, [journey]);

  const makeShare = async () => {
    try { const { data } = await api.post(`/projects/${projectId}/share-link`, { ttl_days: 14 });
      toast.success("Read-only investor/auditor link created"); setLinks(l => [data, ...l]); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (t) => {
    try { await api.post(`/share-links/${t}/revoke`); setLinks(l => l.filter(x => x.token !== t)); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const inviteReviewer = async () => {
    try { await api.post(`/reviewers/invite`, { project_id: projectId, email: invite });
      toast.success(`Reviewer invited: ${invite}`); setInvite(""); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doSwitch = async () => {
    try { const { data } = await api.post(`/projects/${projectId}/switch-market`, { new_jurisdictions: switchJur.split(",").map(s => s.trim()).filter(Boolean) });
      setDelta(data); toast.success(`Switch-market quote: ${data.quote.quote_credits} credits (fixed)`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="k-container" data-testid="project-journey" style={{ padding: 22, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Map size={18} color="var(--accent)" />
        <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Compliance journey</h3>
      </div>
      <div className="k-muted" data-testid="tagline-journey" style={{ fontSize: 12, marginBottom: 12 }}>Think Global, App Local</div>

      {/* BW5 timeline */}
      {journey && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }} data-testid="journey-timeline">
          {journey.steps.map((s, i) => (
            <span key={s.key} className={`k-badge ${s.status === "done" ? "k-badge-accent" : ""}`}
              data-testid={`journey-step-${s.key}`} style={{ fontSize: 12 }}>
              {i + 1}. {s.label} <span className={STEP_CLS[s.status]} style={{ fontSize: 10, marginLeft: 4 }}>{s.status}</span>
            </span>
          ))}
        </div>
      )}

      {/* BW4 budget meter */}
      {budget?.has_quote && (
        <div className="k-control" data-testid="build-budget-meter" style={{ marginBottom: 12 }}>
          <div className="k-muted" style={{ fontSize: 12 }}>{budget.percent_used}% of your quoted build budget used ({Number(budget.used_credits).toLocaleString("en-GB")}/{Number(budget.quoted_credits).toLocaleString("en-GB")} credits)</div>
          <div style={{ height: 8, background: "var(--control)", borderRadius: 6, marginTop: 6, overflow: "hidden" }}>
            <div style={{ width: `${budget.percent_used}%`, height: "100%", background: "var(--accent-primary)" }} />
          </div>
          <div className="k-muted" style={{ fontSize: 11, marginTop: 4 }}>{budget.note}</div>
        </div>
      )}

      {/* BW6 access-verification nudge */}
      {nudge?.unverified > 0 && (
        <p className="k-tag-warn" data-testid="access-nudge" style={{ fontSize: 12, padding: 10, borderRadius: 8, marginBottom: 12 }}>
          <ShieldCheck size={13} /> {nudge.message} <span className="k-muted" style={{ display: "block", fontSize: 11, marginTop: 4 }}>{nudge.note}</span>
        </p>
      )}

      {/* BW3 switch market */}
      <div style={{ marginBottom: 12 }} data-testid="switch-market">
        {showSwitchTip && (
          <div className="k-container" data-testid="switch-market-tip" style={{ padding: 12, marginBottom: 10, borderLeft: "3px solid var(--accent-primary)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Globe2 size={15} color="var(--accent-primary)" />
            <span className="k-muted" style={{ fontSize: 13, flex: "1 1 220px" }}>
              Built for one market? Take the same app to another with <strong className="k-heading">Switch market</strong> below — fixed-price, same margin floor.
            </span>
            <button className="k-btn k-btn-ghost" data-testid="switch-tip-dismiss" style={{ padding: "6px 12px", fontSize: 13 }} onClick={dismissSwitchTip}>Got it</button>
          </div>
        )}
        <div className="k-heading" style={{ fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center" }}>
          <Globe2 size={13} /> <span style={{ marginLeft: 4 }}>Switch market (fixed-price)</span>
          <InfoTip label="How does Switch Market pricing work?" testid="switch-market-tip"
            text="Switching market is a fixed-scope build like any other: it runs through the same Cost Guard and 60% margin floor, never a discounted or parallel path. You always see a price estimate before anything is charged." />
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <input className="k-control" data-testid="switch-jur" placeholder="Target region codes e.g. KE, IN" value={switchJur}
            onChange={e => setSwitchJur(e.target.value)} style={{ fontSize: 13, flex: 1 }} />
          <button className="k-btn k-btn-primary" data-testid="switch-quote" disabled={!switchJur.trim()} onClick={doSwitch}>Quote</button>
        </div>
        {delta && (
          <div className="k-control" data-testid="switch-delta" style={{ marginTop: 8, fontSize: 12 }}>
            <div className="k-muted">New connectors: {delta.delta.new_connector_total} · Added layers: {delta.delta.added_layers.length} · Removed: {delta.delta.removed_layers.length}</div>
            <div className="k-muted" style={{ marginTop: 4 }}>{delta.note}</div>
          </div>
        )}
      </div>

      {/* BW10 reviewer invite from day one */}
      <div style={{ marginBottom: 12 }} data-testid="reviewer-invite">
        <div className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}><UserPlus size={13} /> Invite a reviewer (day one)</div>
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <input className="k-control" data-testid="reviewer-email" placeholder="counsel@example.com" value={invite}
            onChange={e => setInvite(e.target.value)} style={{ fontSize: 13, flex: 1 }} />
          <button className="k-btn k-btn-ghost" data-testid="reviewer-invite-btn" disabled={!invite.trim()} onClick={inviteReviewer}>Invite</button>
        </div>
      </div>

      {/* BW7 investor/auditor share link + BW2 passport + BW12 badge */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="k-btn k-btn-ghost" data-testid="make-share-link" onClick={makeShare}><Link2 size={13} /> Create investor/auditor link</button>
        <button className="k-btn k-btn-ghost" data-testid="view-passport" onClick={() => api.get(`/projects/${projectId}/passport`).then(r => setPassport(r.data)).catch(() => {})}><ShieldCheck size={13} /> Compliance passport</button>
        <a className="k-btn k-btn-ghost" data-testid="build-badge-link" href={`${API}/api/projects/${projectId}/build-badge.svg`} target="_blank" rel="noreferrer"><Award size={13} /> Build badge</a>
      </div>
      {links.length > 0 && (
        <div style={{ marginTop: 10 }} data-testid="share-links">
          {links.map(l => (
            <div key={l.token} className="k-muted" style={{ fontSize: 12, display: "flex", gap: 8, alignItems: "center" }}>
              <code style={{ fontSize: 11 }}>/shared/{l.token.slice(0, 12)}…</code> expires {String(l.expires_at).slice(0, 10)}
              <button className="k-link" data-testid={`revoke-${l.token.slice(0,8)}`} onClick={() => revoke(l.token)}>revoke</button>
            </div>
          ))}
        </div>
      )}
      {passport && (
        <div className="k-control" data-testid="passport-panel" style={{ marginTop: 10, fontSize: 12 }}>
          <div className="k-heading" style={{ fontSize: 14, fontWeight: 700 }}>{passport.project.name} — Compliance Passport</div>
          <div className="k-muted" style={{ marginTop: 4 }}>{passport.domain_label} · {passport.scope_statement}</div>
          <div className="k-muted" style={{ marginTop: 4 }}>{passport.connector_count} connectors · evidence pack: {passport.evidence_pack_status}</div>
          <div style={{ marginTop: 6 }}>{passport.layers.map(l => <span key={l.id} className="k-badge" style={{ marginRight: 4, marginBottom: 4 }}>{l.name}</span>)}</div>
          <p className="k-tag-warn" style={{ fontSize: 12, padding: 8, borderRadius: 8, marginTop: 8 }}>{passport.disclaimer}</p>
          <div className="k-muted" style={{ fontSize: 11 }}>{passport.tagline} · {passport.beco_note}</div>
        </div>
      )}
    </div>
  );
}
